import { useCallback, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import DialogWrapper from './Wrapper';
import useApp from '../../../hooks/useApp';
import { getErrorMessage } from '../../../lib/error';
import { getSDKClient } from '../../../lib/sdk';

type PaymentUser = {
  senderName: string;
  externalUserId: string;
  paymentUserId: string;
  customerAppId: string;
  autopayConsent: boolean;
};

// UpdatePaymentUser is a dialog to allow edit user (autopay consent only for now)
export default function UpdatePaymentUser({
  title,
  open,
  handleClose,
  handleCloseWithRefresh,
  paymentUser,
}: {
  title: string;
  open: boolean;
  handleClose: () => void;
  handleCloseWithRefresh: () => void;
  paymentUser: PaymentUser;
}) {
  const { app } = useApp();
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [currentConsent, setCurrentConsent] = useState(paymentUser.autopayConsent); // current autopay consent value
  const [confirm, setConfirm] = useState(false); // whether the user confirmed to get the consent from the payment user
  const [success, setSuccess] = useState(false); // if true show the success message
  const [failure, setFailure] = useState(false); // if true show the failure message
  const [errMsg, setErrMsg] = useState('');

  const [token, setToken] = useState<string>('');
  const [env, setEnv] = useState<string>('');
  const handleConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentConsent(event.target.checked);
  };

  const handleUserConsent = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirm(event.target.checked);
  };

  const resetStateAndClose = () => {
    setConfirm(false);
    setSuccess(false);
    setFailure(false);
    setErrMsg('');
    handleClose();
    setTimeout(() => {
      // do this after 100ms to avoid weird animation when the dialog is closing
      setButtonEnabled(false);
      setCurrentConsent(paymentUser.autopayConsent);
    }, 100);
  };

  const updatePaymentUser = useCallback(
    async (customerAppId: string, paymentUserId: string, consent: boolean) => {
      setButtonEnabled(false);
      setFailure(false); // hide the failure message
      const sdk = getSDKClient(env, token);
      try {
        await sdk.updatePaymentUser(customerAppId, paymentUserId, {
          autopay_consent: consent,
        });
        setSuccess(true);
        setTimeout(handleCloseWithRefresh, 3000); // show the message for 3 seconds
      } catch (err) {
        setErrMsg(getErrorMessage(err));
        setFailure(true);
      }
    },
    [env, token, handleCloseWithRefresh],
  );

  useEffect(() => {
    const { tokens, env } = app;
    const currentToken = tokens.get(env) ?? '';
    setEnv(env);
    setToken(currentToken);
  }, [app, paymentUser]);

  useEffect(() => {
    if (paymentUser.autopayConsent) {
      // if original autopay consent is true then enable the button if the current value is different than the original
      setButtonEnabled(paymentUser.autopayConsent !== currentConsent);
    } else {
      // if original autopay consent is false then enable the button if the current value is different than the original and the user has confirmed consent
      setButtonEnabled(confirm && paymentUser.autopayConsent !== currentConsent);
    }
  }, [paymentUser.autopayConsent, currentConsent, confirm]);

  return (
    <DialogWrapper
      id="update-user"
      onClose={resetStateAndClose}
      aria-labelledby="update-user"
      title={title}
      open={open}
    >
      <DialogContent dividers>
        <FormGroup>
          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: 'center',
            }}
          >
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">Autopay consent was updated successfully.</Alert>
              </Grid>
            )}
            {failure && (
              <Grid item xs={12}>
                <Alert severity="error">{errMsg}</Alert>
              </Grid>
            )}
            <Grid item xs={4}>
              Sender:
            </Grid>
            <Grid item xs={8}>
              {paymentUser.senderName}
            </Grid>
            <Grid item xs={4}>
              External User ID:
            </Grid>
            <Grid item xs={8}>
              {paymentUser.externalUserId}
            </Grid>
            <Grid item xs={4}>
              Autopay Consent:
            </Grid>
            <Grid item xs={8}>
              <FormControlLabel
                label=""
                control={
                  <Switch
                    sx={{
                      top: '-5px',
                      '& span span': {
                        boxShadow:
                          '1px 3px 1px 0px rgba(0,0,0,0.4),-2px 1px 1px 0px rgba(0,0,0,0.28),1px -1px 1px 0px rgba(0,0,0,0.2)',
                      },
                    }}
                    checked={currentConsent}
                    onChange={handleConsentChange}
                  />
                }
              />
            </Grid>
            {!paymentUser.autopayConsent && (
              <Grid item xs={12}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start' }}
                  label={
                    <Typography variant="body2" component="div">
                      I hereby represent and warrant that I have received the user&apos;s explicit consent to turn on
                      autopay
                    </Typography>
                  }
                  control={<Checkbox sx={{ padding: '0 9px' }} checked={confirm} onChange={handleUserConsent} />}
                />
              </Grid>
            )}
            <Grid
              container
              item
              xs={12}
              sx={{
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" component="div">
                When autopay consent is enabled, Finverse will automatically collect payments on each bill due date
                (note: requires Finverse connection to merchant&apos;s billing data or ERP)
              </Typography>
            </Grid>
            <Grid
              container
              item
              xs={12}
              sx={{
                justifyContent: 'center',
              }}
            >
              <Grid>
                <Button
                  variant="contained"
                  disabled={!buttonEnabled}
                  onClick={() =>
                    updatePaymentUser(paymentUser.customerAppId, paymentUser.paymentUserId, currentConsent)
                  }
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </FormGroup>
      </DialogContent>
    </DialogWrapper>
  );
}
